import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { NotificationService } from 'src/app/services/notification.service';
import { LogError, LogStatus } from '@memberjunction/core';
import { FileRecord } from 'src/app/utils/interfaces';
import { DocumentManagerGridColumns } from 'src/app/utils/grid-constants';
import { Utils } from 'src/app/utils/utils';

@Component({
  selector: 'acs-document-manager',
  templateUrl: './acs-document-manager.html',
  styleUrls: ['./acs-document-manager.scss']
})
export class AcsDocumentManagerComponent implements OnInit {
  private fileStorageService = inject(FileStorageService);
  private notificationService = inject(NotificationService);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  utils = inject(Utils);

  // User document repository - no chapter inputs needed

  // Data Grid Configuration
  documents: FileRecord[] = [];
  columns = DocumentManagerGridColumns;
  isLoading = false;

  // Category and view filtering
  categories: Array<{name: string, displayName: string}> = [];
  selectedCategory = 'All';
  searchTerm = '';
  
  // Upload modal
  isUploadModalOpen = false;
  
  // Preview modal
  isPreviewModalOpen = false;
  currentPreviewDocument: FileRecord | null = null;
  previewUrl = '';
  safePreviewUrl: SafeResourceUrl | null = null;
  previewType: 'pdf' | 'image' | 'text' | 'video' | 'unsupported' = 'unsupported';
  isLoadingPreview = false;
  
  ngOnInit(): void {
    this.setupDataGrid();
    this.loadCategories();
    this.loadDocuments();
  }

  private setupDataGrid(): void {
    // Columns are now imported from grid-constants.ts
    // Update action handlers to use component methods
    this.columns = DocumentManagerGridColumns.map(col => {
      if (col.key === 'actions' && col.actions) {
        return {
          ...col,
          actions: col.actions.map((action, index) => ({
            ...action,
            handler: index === 0
              ? (item: FileRecord) => this.openPreview(item)
              : index === 1
                ? (item: FileRecord) => this.downloadDocument(item)
                : (item: FileRecord) => this.deleteDocument(item)
          }))
        };
      }
      // Update Category column to use dynamic categories
      if (col.key === 'Category') {
        return {
          ...col,
          format: (value: string) => this.getCategoryDisplayNameDynamic(value || ''),
          formatForFilter: (value: string) => this.getCategoryDisplayNameDynamic(value || '')
        };
      }
      return col;
    });
  }

  /**
   * Get category display name using loaded categories from service
   * Falls back to utils function if categories not loaded yet
   */
  private getCategoryDisplayNameDynamic(categoryName: string): string {
    if (!categoryName) return '';

    // Try to find in loaded categories first
    const category = this.categories.find(cat => cat.name === categoryName);
    if (category) {
      return category.displayName;
    }

    // Fallback to utils function for backwards compatibility
    return this.utils.getCategoryDisplayName(categoryName);
  }

  private async loadCategories(): Promise<void> {
    try {
      this.fileStorageService.getFileCategories().subscribe({
        next: (categories) => {
          // Store full category objects with name and displayName
          this.categories = categories;
          // Categories loaded successfully
        },
        error: (error) => {
          LogError('Failed to load categories:', undefined, error);
          // Fallback categories to match API response
          this.categories = [
            { name: 'general-documents', displayName: 'General Documents' },
            { name: 'announcements', displayName: 'Announcements' },
            { name: 'meeting-materials', displayName: 'Meeting Materials' },
            { name: 'financial-reports', displayName: 'Financial Reports' },
            { name: 'personal', displayName: 'Personal' },
            { name: 'work', displayName: 'Work' },
            { name: 'photos', displayName: 'Photos' }
          ];
        }
      });
    } catch (error) {
      LogError('Error loading categories:', undefined, error);
    }
  }

  async loadDocuments(): Promise<void> {
    this.isLoading = true;

    try {
      // Find the actual category name to use for filtering
      let categoryFilter: string | undefined = undefined;
      if (this.selectedCategory && this.selectedCategory !== 'All') {
        // Find the category object by display name
        const categoryObj = this.categories.find(cat => cat.displayName === this.selectedCategory);
        categoryFilter = categoryObj ? categoryObj.name : this.selectedCategory;
      }

      // Load user documents using the file storage service
      this.fileStorageService.getUserFiles(categoryFilter, this.searchTerm).subscribe({
        next: (response) => {
          let documents = response.files;
          this.documents = documents;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          LogError('Failed to load documents:', undefined, error);
          this.notificationService.error('Loading Error', 'Failed to load documents');
          this.isLoading = false;
        }
      });
    } catch (error) {
      LogError('Error loading documents:', undefined, error);
      this.isLoading = false;
    }
  }

  // Grid helper methods (getCategoryDisplayName removed - using Category field directly from view)

  private canPreviewFile(file: FileRecord): boolean {
    const mimeType = file.ContentType.toLowerCase();
    return mimeType.startsWith('image/') || 
           mimeType === 'application/pdf' || 
           mimeType.startsWith('text/') ||
           mimeType.startsWith('video/');
  }

  // File actions
  async openPreview(document: FileRecord): Promise<void> {
    if (!this.canPreviewFile(document)) {
      this.notificationService.warning('Preview Unavailable', 'This file type cannot be previewed');
      return;
    }

    try {
      // Open modal immediately with loading state
      this.currentPreviewDocument = document;
      this.previewType = this.getPreviewType(document.ContentType);
      this.isPreviewModalOpen = true;
      this.isLoadingPreview = true;
      this.previewUrl = '';
      this.safePreviewUrl = null;

      // Trigger change detection to ensure modal opens immediately
      this.cdr.detectChanges();

      // Log access first
      this.fileStorageService.logDocumentAccess(document.ID, 'View');
      LogStatus(`Logging preview access for document: ${document.Name}`);
      
      // Use pre-loaded URL if available, otherwise fetch it
      if (document.DownloadUrl) {
        this.setPreviewUrl(document.DownloadUrl);
      } else {
        // Fallback: fetch URL on demand
        this.fileStorageService.getDownloadUrl(document.ID).subscribe({
          next: (response) => {
            if (response?.downloadUrl) {
              this.setPreviewUrl(response.downloadUrl);
            } else {
              this.isLoadingPreview = false;
              this.notificationService.error('Preview Error', 'Unable to generate preview URL');
            }
          },
          error: (error) => {
            this.isLoadingPreview = false;
            LogError('Preview failed:', undefined, error);
            this.notificationService.error('Preview Error', 'Failed to load document preview');
          }
        });
      }
    } catch (error) {
      this.isLoadingPreview = false;
      LogError('Document preview failed:', undefined, error);
      this.notificationService.error('Preview Error', 'Preview failed');
    }
  }

  private setPreviewUrl(url: string): void {
    this.previewUrl = url;
    
    // Sanitize URL for iframe/video/src usage
    if (this.previewType === 'pdf' || this.previewType === 'text') {
      this.safePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    
    this.isLoadingPreview = false;
  }

  onCategoryChange(): void {
    this.loadDocuments();
  }


  async downloadDocument(document: FileRecord): Promise<void> {
    try {
      // Log access first
      await this.fileStorageService.logDocumentAccess(document.ID, 'Download');
      LogStatus(`Logging download access for document: ${document.Name}`);
      
      // Get download URL
      this.fileStorageService.getDownloadUrl(document.ID).subscribe({
        next: async (response) => {
          if (response?.downloadUrl) {
            try {
              // Fetch the file as a blob for proper download
              const fileResponse = await fetch(response.downloadUrl);
              if (!fileResponse.ok) {
                throw new Error(`Failed to fetch file: ${fileResponse.status}`);
              }
              
              const blob = await fileResponse.blob();
              const fileName = document.OriginalName || document.Name;
              
              // Create download link with blob URL
              const blobUrl = window.URL.createObjectURL(blob);
              const link = window.document.createElement('a');
              link.href = blobUrl;
              link.download = fileName;
              link.style.display = 'none';
              
              // Trigger download
              window.document.body.appendChild(link);
              link.click();
              window.document.body.removeChild(link);
              
              // Clean up blob URL
              window.URL.revokeObjectURL(blobUrl);
              
              this.notificationService.success('Download Started', `Downloading ${fileName}`);
              LogStatus(`Successfully initiated download for: ${fileName}`);
            } catch (fetchError) {
              LogError('File fetch failed:', undefined, fetchError);
              // Fallback to direct link if blob download fails
              window.open(response.downloadUrl, '_blank');
              this.notificationService.success('Download Started', `Opening ${document.Name}`);
            }
          } else {
            throw new Error('Download URL not available');
          }
        },
        error: (error) => {
          LogError('Download failed:', undefined, error);
          this.notificationService.error('Download Error', 'Download failed');
        }
      });
    } catch (error) {
      LogError('Document download failed:', undefined, error);
      this.notificationService.error('Download Error', 'Download failed');
    }
  }

  async deleteDocument(document: FileRecord): Promise<void> {
    if (!confirm(`Are you sure you want to delete "${document.Name}"?`)) {
      return;
    }
    
    try {
      // Log access first
      await this.fileStorageService.logDocumentAccess(document.ID, 'Delete');
      LogStatus(`Logging delete access for document: ${document.Name}`);
      
      this.fileStorageService.deleteFile(document.ID).subscribe({
        next: (response) => {
          if (response.success) {
            this.notificationService.success('Delete Successful', 'Document deleted successfully');
            LogStatus(`Successfully deleted document: ${document.Name}`);
            this.loadDocuments(); // Refresh list
          } else {
            throw new Error('Delete operation failed');
          }
        },
        error: (error) => {
          LogError('Delete failed:', undefined, error);
          this.notificationService.error('Delete Error', 'Delete failed');
        }
      });
    } catch (error) {
      LogError('Document deletion failed:', undefined, error);
      this.notificationService.error('Delete Error', 'Delete failed');
    }
  }

  openUploadModal(): void {
    this.isUploadModalOpen = true;
    this.cdr.detectChanges();
  }

  onUploadModalClosed(): void {
    this.isUploadModalOpen = false;
    this.cdr.detectChanges();
  }

  onDocumentsUploaded(): void {
    this.isUploadModalOpen = false;
    this.loadDocuments(); // Refresh list
    this.notificationService.success('Upload Successful', 'Documents uploaded successfully');
    this.cdr.detectChanges();
  }

  // Helper methods moved to Utils service
  
  getTotalStorageUsed(): string {
    const totalBytes = this.documents.reduce((sum, doc) => sum + (doc.Size || 0), 0);
    return this.utils.formatFileSize(totalBytes);
  }

  getCategoryIcon(category: string): string {
    return this.utils.getCategoryIcon(category);
  }

  getPreviewType(contentType: string): 'pdf' | 'image' | 'text' | 'video' | 'unsupported' {
    const type = contentType.toLowerCase();
    
    if (type === 'application/pdf') {
      return 'pdf';
    } else if (type.startsWith('image/')) {
      return 'image';
    } else if (type.startsWith('text/')) {
      return 'text';
    } else if (type.startsWith('video/')) {
      return 'video';
    } else {
      return 'unsupported';
    }
  }

  closePreview(): void {
    this.isPreviewModalOpen = false;
    this.currentPreviewDocument = null;
    this.previewUrl = '';
    this.safePreviewUrl = null;
    this.previewType = 'unsupported';
    this.isLoadingPreview = false;
    
    // Trigger change detection to ensure state is properly updated
    this.cdr.detectChanges();
  }

  downloadFromPreview(): void {
    if (this.currentPreviewDocument) {
      this.downloadDocument(this.currentPreviewDocument);
    }
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    LogError('Image preview failed to load:', undefined, img.src);
    this.notificationService.warning('Preview Error', 'Unable to load image preview');
  }
  
  getDocumentCount(): number {
    return this.documents.length;
  }

  get categoryDisplayNames(): string[] {
    return ['All', ...this.categories.map(cat => cat.displayName)];
  }
}